import { Box, Button, FormControl, FormLabel, Select } from '@chakra-ui/react'
import { InsuranceParamsVehiclePagedQuery } from 'BrokerTsLib/src/dtos'
import { useContext } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Card, CardBody } from '../../../components/Card'

import { Datepicker } from '../../../components/Datepicker'
import { InputGroup } from '../../../components/InputGroup'
import { Loading } from '../../../components/Loading'
import { useInsurers } from '../../../hooks'
import { useGetOrganizationUnits, useGetUsers } from '../../../hooks/useUser'
import { Search } from '../../../icons'
import { contractsCarsContext } from '../../../pages/carkulka/contracts'
import { conceptsCarsContext } from '../../../pages/carkulka/concepts'
import { SearchWrapper } from '../SearchWrapper'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'

interface CarSearchFormProps {
  onSubmit: (search: InsuranceParamsVehiclePagedQuery) => void,
  filter: InsuranceParamsVehiclePagedQuery,
  listType: 'contracted' | 'opened'
}

export const CarSearchForm = ({ onSubmit, filter, listType }: CarSearchFormProps) => {
  const { setCurrentPage, loading: loadingList } = useContext(listType == 'contracted' ? contractsCarsContext : conceptsCarsContext)

  const methods = useForm<InsuranceParamsVehiclePagedQuery>({
    defaultValues: filter,
  })

  const { register, getValues, setValue, handleSubmit, formState: { isSubmitting } } = methods

  const { data: allInsurers, isFetched: isFetchedInsurers } = useInsurers(null, listType == 'contracted')
  const { data: organizationUnits, isFetched: isFetchedOrganizationUnits } = useGetOrganizationUnits()
  const { data: users, isFetched: isFetchedUsers } = useGetUsers()

  const loading = !isFetchedInsurers || !isFetchedUsers || !isFetchedOrganizationUnits

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit((filter) => {
        setCurrentPage(1)
        return onSubmit(filter)
      })}>
        {loading &&
          <Card>
            <CardBody>
              <Loading large={true} />
            </CardBody>
          </Card>
        }

        {!loading &&
          <SearchWrapper>

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="offerNumber">Číslo nabídky</FormLabel>

              <InputGroup id="offerNumber" placeholder="Např: 73622" {...register('insuranceParamsId')} />
            </FormControl>

            {listType == 'contracted' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="contractNumber">Číslo smlouvy</FormLabel>

                  <InputGroup id="contractNumber" placeholder="Např: 73622" {...register('contractNumber')} />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateFrom">Datum sjednání od</FormLabel>

                  <Datepicker
                    date={getValues('dateContractedFrom')}
                    onDateChange={(date) => setValue('dateContractedFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateTo">Datum sjednání do</FormLabel>

                  <Datepicker
                    date={getValues('dateContractedTo')}
                    onDateChange={(date) => setValue('dateContractedTo', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel>Datum výročí od</FormLabel>

                  <Datepicker
                    date={getValues('anniversaryFrom')}
                    onDateChange={(date) => setValue('anniversaryFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel>Datum výročí do</FormLabel>

                  <Datepicker
                    date={getValues('anniversaryTo')}
                    onDateChange={(date) => setValue('anniversaryTo', formatDate(date))}
                  />
                </FormControl>
              </>
            }

            {listType == 'opened' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateFrom">Datum od</FormLabel>

                  <Datepicker
                    date={getValues('dateCreatedFrom')}
                    onDateChange={(date) => setValue('dateCreatedFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateTo">Datum do</FormLabel>

                  <Datepicker
                    date={getValues('dateCreatedTo')}
                    onDateChange={(date) => setValue('dateCreatedTo', formatDate(date))}
                  />
                </FormControl>
              </>
            }

            {(allInsurers?.data.length ?? 0) > 0 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="insuranceCompany">Pojišťovna</FormLabel>

                <Select {...register('insurerId')}>
                  <option value=''>
                    Vyberte
                  </option>
                  {allInsurers?.data.map((company) => {
                    return (
                      <option key={company.id} value={company.id.toString()}>
                        {company.description}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="spz">Registrační značka</FormLabel>

              <InputGroup id="spz" placeholder="Např: 1A1 0000" {...register('registrationMark')} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="vin">VIN</FormLabel>

              <InputGroup id="vin" placeholder="Např: 2HNYD188068515943" {...register('vin')} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="insureeName">Pojistník (příjmení/název firmy)</FormLabel>

              <InputGroup id="insureeName" placeholder="Např: Novák" {...register('legalPersonName')} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="insureeNumber">Pojistník (rodné číslo/IČO)</FormLabel>

              <InputGroup
                id="insureeNumber"
                placeholder="Např: 9001012233"
                {...register('legalPersonIdentification')}
              />
            </FormControl>

            {(organizationUnits?.data.length ?? 0) > 1 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="organization">Organizační jednotka</FormLabel>

                <Select {...register('ouId')}>
                  <option value=''>
                    Vyberte organizaci
                  </option>
                  {organizationUnits?.data.map((company) => {
                    return (
                      <option key={company.id} value={company.id.toString()}>
                        {company.name}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            {(users?.data.length ?? 0) > 1 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="user">Uživatel</FormLabel>

                <Select {...register('userId')}>
                  <option value=''>
                    Vyberte uživatele
                  </option>
                  {users?.data.map((user) => {
                    return (
                      <option key={user.userId} value={user.userId}>
                        {user.surname} {user.name} ({organizationUnits?.data.find(x => x.id.toString() === user.ouId?.toString())?.name})
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 4,
                rowGap: 4,
                alignItems: 'end',
                justifyContent: 'space-between',
                gridColumn: { base: '', md: '3 / 5' },
              }}
            >
              {/* <FormControl>
              <FormLabel>Stav nabídky</FormLabel>

              <CheckboxGroup>
                <HStack spacing={{ base: 4, xl: 8 }} sx={{ minH: '36px' }}>
                  <Checkbox value="created" {...register('offerStatus')}>
                    Vytvořená
                  </Checkbox>
                  <Checkbox value="toSend" {...register('offerStatus')}>
                    K odeslání
                  </Checkbox>
                  <Checkbox value="sent" {...register('offerStatus')}>
                    Odeslaná
                  </Checkbox>
                </HStack>
              </CheckboxGroup>
            </FormControl> */}

              <Button type="submit" colorScheme="primary" isLoading={isSubmitting || loadingList}>
                <Search />
                Vyhledat
              </Button>
            </Box>

          </SearchWrapper>
        }
      </form>
    </FormProvider>
  )
}
